// src/pages/AdminAutoBackup.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const API_BASE =
  process.env.NODE_ENV === "production" ? "" : process.env.REACT_APP_API_BASE || "";

function authHeaders(extra = {}) {
  const token = localStorage.getItem("token") || "";
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function httpJson(url, { method = "GET", body } = {}) {
  const res = await fetch(url, {
    method,
    headers: authHeaders({
      Accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {}),
    }),
    body: body ? JSON.stringify(body) : undefined,
  });
  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    const msg = typeof data === "string" ? data : data?.error || data?.message || "Error";
    throw new Error(msg);
  }
  return data;
}

export default function AdminAutoBackup() {
  const navigate = useNavigate();
  const isAdmin = String(localStorage.getItem("is_admin") || "0") === "1";

  const [status, setStatus] = useState("");
  const [targets, setTargets] = useState([]);
  const [schedules, setSchedules] = useState([]);

  const [targetForm, setTargetForm] = useState({
    id: null,
    name: "",
    host: "",
    port: 3306,
    user: "",
    password: "",
    db_name: "",
  });

  const [scheduleForm, setScheduleForm] = useState({
    id: null,
    target_id: "",
    enabled: true,
    cron_expr: "0 0 * * *",
    timezone: "Europe/Sofia",
    mode: "mirror",
    create_db: true,
    keep_local_days: "",
  });

  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const targetById = useMemo(() => {
    const m = new Map();
    (targets || []).forEach((t) => m.set(String(t.id), t));
    return m;
  }, [targets]);

  const loadAll = async () => {
    setStatus("Зареждане...");
    try {
      const t = await httpJson(`${API_BASE}/api/admin/backup/targets`);
      const s = await httpJson(`${API_BASE}/api/admin/backup/schedules`);
      setTargets(t?.targets || []);
      setSchedules(s?.schedules || []);
      setStatus("");
    } catch (e) {
      setStatus(e.message || "Грешка");
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const resetTargetForm = () =>
    setTargetForm({ id: null, name: "", host: "", port: 3306, user: "", password: "", db_name: "" });

  const resetScheduleForm = () =>
    setScheduleForm({
      id: null,
      target_id: "",
      enabled: true,
      cron_expr: "0 0 * * *",
      timezone: "Europe/Sofia",
      mode: "mirror",
      create_db: true,
      keep_local_days: "",
    });

  const saveTarget = async () => {
    try {
      setStatus("Запис на target...");
      const body = {
        name: targetForm.name,
        host: targetForm.host,
        port: Number(targetForm.port || 3306),
        user: targetForm.user,
        password: targetForm.password,
        db_name: targetForm.db_name,
      };
      if (targetForm.id) {
        await httpJson(`${API_BASE}/api/admin/backup/targets/${targetForm.id}`, { method: "PUT", body });
      } else {
        await httpJson(`${API_BASE}/api/admin/backup/targets`, { method: "POST", body });
      }
      resetTargetForm();
      await loadAll();
      setStatus("✅ Запазено");
      setTimeout(() => setStatus(""), 1200);
    } catch (e) {
      setStatus(e.message || "Грешка");
    }
  };

  const testTarget = async (id) => {
    try {
      setStatus("Тест на връзка...");
      const r = await httpJson(`${API_BASE}/api/admin/backup/targets/${id}/test`, { method: "POST" });
      setStatus(`✅ OK · MySQL: ${r?.mysql_version || "?"}`);
      setTimeout(() => setStatus(""), 2000);
    } catch (e) {
      setStatus(`❌ ${e.message || "Test failed"}`);
    }
  };

  const deleteTarget = async (id) => {
    if (!window.confirm("Да изтрия ли този target? (ще изтрие и неговите графици)") ) return;
    try {
      setStatus("Изтриване...");
      await httpJson(`${API_BASE}/api/admin/backup/targets/${id}`, { method: "DELETE" });
      await loadAll();
      setStatus("✅ Изтрито");
      setTimeout(() => setStatus(""), 1200);
    } catch (e) {
      setStatus(e.message || "Грешка");
    }
  };

  const saveSchedule = async () => {
    try {
      setStatus("Запис на график...");
      const body = {
        target_id: Number(scheduleForm.target_id),
        enabled: !!scheduleForm.enabled,
        cron_expr: scheduleForm.cron_expr,
        timezone: scheduleForm.timezone || null,
        mode: scheduleForm.mode,
        create_db: !!scheduleForm.create_db,
        keep_local_days:
          scheduleForm.keep_local_days === "" ? null : Number(scheduleForm.keep_local_days),
      };
      if (!body.target_id) throw new Error("Избери target");
      if (scheduleForm.id) {
        await httpJson(`${API_BASE}/api/admin/backup/schedules/${scheduleForm.id}`, { method: "PUT", body });
      } else {
        await httpJson(`${API_BASE}/api/admin/backup/schedules`, { method: "POST", body });
      }
      resetScheduleForm();
      await loadAll();
      setStatus("✅ Запазено");
      setTimeout(() => setStatus(""), 1200);
    } catch (e) {
      setStatus(e.message || "Грешка");
    }
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm("Да изтрия ли този график?") ) return;
    try {
      setStatus("Изтриване...");
      await httpJson(`${API_BASE}/api/admin/backup/schedules/${id}`, { method: "DELETE" });
      await loadAll();
      setStatus("✅ Изтрито");
      setTimeout(() => setStatus(""), 1200);
    } catch (e) {
      setStatus(e.message || "Грешка");
    }
  };

  const startPolling = (id) => {
    setJobId(id);
    setJob(null);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await httpJson(`${API_BASE}/api/admin/backup/status/${id}`);
        setJob(r?.job || null);
        if (r?.job?.status === "success" || r?.job?.status === "error") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          // refresh schedules to get last_run fields
          loadAll();
        }
      } catch {
        // ignore
      }
    }, 2000);
  };

  const runNowSchedule = async (schedule_id) => {
    try {
      setStatus("Старт на бекъп...");
      const r = await httpJson(`${API_BASE}/api/admin/backup/run`, {
        method: "POST",
        body: { schedule_id },
      });
      setStatus("✅ Стартирано");
      startPolling(r?.jobId);
      setTimeout(() => setStatus(""), 1200);
    } catch (e) {
      setStatus(e.message || "Грешка");
    }
  };

  const runNowTarget = async (target_id) => {
    try {
      setStatus("Старт на бекъп...");
      const r = await httpJson(`${API_BASE}/api/admin/backup/run`, {
        method: "POST",
        body: { target_id, mode: "mirror", create_db: true },
      });
      setStatus("✅ Стартирано");
      startPolling(r?.jobId);
      setTimeout(() => setStatus(""), 1200);
    } catch (e) {
      setStatus(e.message || "Грешка");
    }
  };

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Само администратор може да използва тези инструменти.
        </Alert>
        <Button variant="contained" onClick={() => navigate("/admin/dashboard")}>Назад</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">🕒 Автоматичен бекъп (Remote DB)</Typography>
        <Button variant="outlined" onClick={() => navigate("/admin/dashboard")}>НАЗАД</Button>
      </Stack>

      {status ? (
        <Alert severity={String(status).startsWith("❌") ? "error" : "info"} sx={{ mb: 2 }}>
          {status}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>1) Remote targets (къде да се копира)</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Stack spacing={1}>
              {(targets || []).length ? (
                (targets || []).map((t) => (
                  <Paper key={t.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} justifyContent="space-between">
                      <Box>
                        <Typography fontWeight={700}>{t.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t.host}:{t.port} · user: {t.user} · db: {t.db_name}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" onClick={() => testTarget(t.id)}>TEST</Button>
                        <Button size="small" variant="outlined" onClick={() => runNowTarget(t.id)}>RUN NOW</Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setTargetForm({
                              id: t.id,
                              name: t.name || "",
                              host: t.host || "",
                              port: t.port || 3306,
                              user: t.user || "",
                              password: "", // don’t show saved pass
                              db_name: t.db_name || "",
                            });
                          }}
                        >
                          EDIT
                        </Button>
                        <Button size="small" color="error" onClick={() => deleteTarget(t.id)}>DELETE</Button>
                      </Stack>
                    </Stack>
                  </Paper>
                ))
              ) : (
                <Alert severity="info">Няма добавени targets.</Alert>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography fontWeight={700} sx={{ mb: 1 }}>{targetForm.id ? "Edit target" : "Add target"}</Typography>
              <Stack spacing={1.2}>
                <TextField size="small" label="Name" value={targetForm.name} onChange={(e) => setTargetForm((s) => ({ ...s, name: e.target.value }))} />
                <TextField size="small" label="Host / IP" value={targetForm.host} onChange={(e) => setTargetForm((s) => ({ ...s, host: e.target.value }))} />
                <TextField size="small" label="Port" type="number" value={targetForm.port} onChange={(e) => setTargetForm((s) => ({ ...s, port: e.target.value }))} />
                <TextField size="small" label="User" value={targetForm.user} onChange={(e) => setTargetForm((s) => ({ ...s, user: e.target.value }))} />
                <TextField
                  size="small"
                  label={targetForm.id ? "Password (leave empty = keep)" : "Password"}
                  type="password"
                  value={targetForm.password}
                  onChange={(e) => setTargetForm((s) => ({ ...s, password: e.target.value }))}
                />
                <TextField size="small" label="Database" value={targetForm.db_name} onChange={(e) => setTargetForm((s) => ({ ...s, db_name: e.target.value }))} />

                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={saveTarget}>SAVE</Button>
                  <Button variant="text" onClick={resetTargetForm}>CLEAR</Button>
                </Stack>

                <Typography variant="caption" color="text.secondary">
                  ⚠️ За cPanel често трябва да разрешиш Remote MySQL за IP-то на VPS.
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>2) Графици (кога да се прави)</Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Stack spacing={1}>
              {(schedules || []).length ? (
                (schedules || []).map((s) => (
                  <Paper key={s.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} justifyContent="space-between">
                      <Box>
                        <Typography fontWeight={700}>
                          #{s.id} · {s.target_name}
                          {Number(s.enabled) ? "" : " (disabled)"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          cron: <b>{s.cron_expr}</b> · tz: {s.timezone || "(server)"} · mode: {s.mode}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          last: {s.last_run_at ? String(s.last_run_at) : "—"} · {s.last_status || "—"}
                          {s.last_error ? ` · ${String(s.last_error).slice(0, 80)}...` : ""}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" onClick={() => runNowSchedule(s.id)}>RUN NOW</Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setScheduleForm({
                              id: s.id,
                              target_id: String(s.target_id),
                              enabled: !!Number(s.enabled),
                              cron_expr: s.cron_expr || "0 0 * * *",
                              timezone: s.timezone || "Europe/Sofia",
                              mode: s.mode || "mirror",
                              create_db: !!Number(s.create_db),
                              keep_local_days: s.keep_local_days == null ? "" : String(s.keep_local_days),
                            });
                          }}
                        >
                          EDIT
                        </Button>
                        <Button size="small" color="error" onClick={() => deleteSchedule(s.id)}>DELETE</Button>
                      </Stack>
                    </Stack>
                  </Paper>
                ))
              ) : (
                <Alert severity="info">Няма графици.</Alert>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography fontWeight={700} sx={{ mb: 1 }}>{scheduleForm.id ? "Edit schedule" : "Add schedule"}</Typography>

              <Stack spacing={1.2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Target</InputLabel>
                  <Select
                    value={scheduleForm.target_id}
                    label="Target"
                    onChange={(e) => setScheduleForm((s) => ({ ...s, target_id: e.target.value }))}
                  >
                    {(targets || []).map((t) => (
                      <MenuItem key={t.id} value={String(t.id)}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={<Switch checked={!!scheduleForm.enabled} onChange={(e) => setScheduleForm((s) => ({ ...s, enabled: e.target.checked }))} />}
                  label="Enabled"
                />

                <TextField
                  size="small"
                  label="Cron expression"
                  value={scheduleForm.cron_expr}
                  onChange={(e) => setScheduleForm((s) => ({ ...s, cron_expr: e.target.value }))}
                  helperText='Примери: "0 0 * * *" (00:00 всеки ден), "*/10 * * * *" (на 10 мин), "0 * * * *" (всеки час)'
                />

                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" onClick={() => setScheduleForm((s) => ({ ...s, cron_expr: "0 0 * * *" }))}>00:00 daily</Button>
                  <Button size="small" variant="outlined" onClick={() => setScheduleForm((s) => ({ ...s, cron_expr: "*/10 * * * *" }))}>every 10 min</Button>
                  <Button size="small" variant="outlined" onClick={() => setScheduleForm((s) => ({ ...s, cron_expr: "0 * * * *" }))}>hourly</Button>
                </Stack>

                <TextField
                  size="small"
                  label="Timezone"
                  value={scheduleForm.timezone}
                  onChange={(e) => setScheduleForm((s) => ({ ...s, timezone: e.target.value }))}
                />

                <FormControl size="small" fullWidth>
                  <InputLabel>Mode</InputLabel>
                  <Select
                    value={scheduleForm.mode}
                    label="Mode"
                    onChange={(e) => setScheduleForm((s) => ({ ...s, mode: e.target.value }))}
                  >
                    <MenuItem value="mirror">mirror (overwrite target)</MenuItem>
                    <MenuItem value="data_only">data_only (no drop)</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={<Switch checked={!!scheduleForm.create_db} onChange={(e) => setScheduleForm((s) => ({ ...s, create_db: e.target.checked }))} />}
                  label="Create DB if missing"
                />

                <TextField
                  size="small"
                  type="number"
                  label="Keep local backups (days)"
                  value={scheduleForm.keep_local_days}
                  onChange={(e) => setScheduleForm((s) => ({ ...s, keep_local_days: e.target.value }))}
                  helperText='По желание. Изисква BACKUP_LOCAL_DIR в backend env.'
                />

                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={saveSchedule}>SAVE</Button>
                  <Button variant="text" onClick={resetScheduleForm}>CLEAR</Button>
                </Stack>

                <Typography variant="caption" color="text.secondary">
                  Забележка: Scheduler-а работи докато backend процесът е стартиран.
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>3) Job статус / логове</Typography>
        {!jobId ? (
          <Alert severity="info">Натисни RUN NOW на target или schedule.</Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Job: <b>{jobId}</b> · status: <b>{job?.status || "..."}</b>
              {job?.target?.name ? ` · target: ${job.target.name}` : ""}
            </Typography>
            {job?.localFile ? (
              <Typography variant="caption" color="text.secondary">Local file: {job.localFile}</Typography>
            ) : null}
            <Divider sx={{ my: 1 }} />
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                bgcolor: "#0b1220",
                color: "#e5e7eb",
                borderRadius: 1,
                overflow: "auto",
                maxHeight: 320,
              }}
            >
              {(job?.logs || []).join("\n") || "(no logs yet)"}
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
